import {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {formatDate, formatSsn, maskSsn} from '@common/lib/utils';
import {CODE_PERMISSIONS} from '@common/permissions';
import {
  useHasPermission,
  useClientActionAllowed,
  usePortalActionAllowed,
} from '@common-web/hooks/use-permissions';
import {STALE_TIME_5_MIN, STALE_TIME_30_SEC} from '@common/constants/time';
import {
  ArrowLeft,
  Building2,
  ChevronDown,
  Crown,
  Download,
  Eye,
  EyeOff,
  FileSpreadsheet,
  Loader2,
  Search,
  Users,
  X,
} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {DatePicker} from '@common-web/components/ui/date-picker';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@common-web/components/ui/collapsible';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Progress} from '@common-web/components/ui/progress';
import {Switch} from '@common-web/components/ui/switch';
import {DataTable} from '@common-web/components/data-table';
import {TableHeaderCount} from '@common-web/components/table-header-count';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import {cn} from '@common-web/lib/utils';
import {initialsFromFullName} from '@common-web/lib/nameUtils';
import {
  CBS_ACTIVE_PILL_CLASS,
  CBS_INACTIVE_PILL_CLASS,
  CBS_TENANT_BADGE_CLASS,
  cbsTenantTint,
} from '@common-web/components/account/cbsAccountsPresentation';
import {RouterOutput, trpc} from '@common-web/lib/trpc';
import {useDebounce} from '@common-web/hooks/use-debounce';
import {useAuthStore} from '@common-web/stores/authStore';
import {usePortal, useIsInternal} from '@common-web/providers/portalProvider';
import {MemberExportDialog} from '@client/components/MemberExportDialog';
import {useBackgroundMemberExport} from '@client/hooks/useBackgroundMemberExport';
import {
  DEFAULT_MEMBER_SEARCH_STATE,
  MEMBER_SEARCH_PAGE_SIZE,
  RELATIONSHIP_OPTIONS,
  memberSearchUrlStateToApiInput,
  useMemberSearchUrlState,
  type MemberSearchActiveStatus,
  type MemberSearchAdvancedSlice,
  type MemberSearchSortKey,
  type MemberSearchUrlState,
} from '@common-web/hooks/useMemberSearchUrlState';
import {VipQuickViewSheet, type QuickViewVip} from '@client/components/VipQuickViewSheet';
import {IN_HOUSE_ADMINISTRATOR} from '@common/constants/administrators';
import {ROUTES} from '@client/lib/routes';
import {useMemberDetailLinkState} from '@client/lib/memberDetailNavigation';

/**
 * Where a row's detail link points.
 *
 * External third-party administrator members have no VBA subscriber behind them — they come from the
 * TPA's eligibility file into the EDI store — so `/member/:subscriberId` would look
 * up a subscriber that doesn't exist. They get their own read-only page instead.
 */
function memberDetailPath(row: {
  externalTpa: string | null;
  externalTpaMemberId: string | null;
  subscriberId: string;
}): string {
  if (row.externalTpa && row.externalTpaMemberId) {
    return ROUTES.externalTpaMember.to(row.externalTpaMemberId);
  }
  return ROUTES.member.to(row.subscriberId);
}

const QUICK_SEARCH_DEBOUNCE_MS = 1000;
const ADVANCED_FILTERS_DEBOUNCE_MS = 500;

/**
 * Calendar bounds for the effective start/end range pickers. Enrollment dates span
 * historical coverage through future-dated effective periods, so allow year-dropdown
 * navigation from 2000 through five years out.
 */
const EFFECTIVE_DATE_CALENDAR_PROPS = {
  captionLayout: 'dropdown' as const,
  startMonth: new Date(2000, 0),
  endMonth: new Date(new Date().getFullYear() + 5, 11),
};

/** Sentinel for Radix Select "all / none" option (empty string is forbidden as a SelectItem value). */
const SELECT_ALL = '__all';
function toSelectValue(v: string): string {
  return v || SELECT_ALL;
}
function fromSelectValue(v: string): string {
  return v === SELECT_ALL ? '' : v;
}

const SORT_LABELS: Record<MemberSearchSortKey, string> = {
  lastName: 'Last name',
  dateOfBirth: 'Date of birth',
  activeDate: 'Active date',
  deactivatedDate: 'Deactivated date',
};

const SORTABLE_KEYS = Object.keys(SORT_LABELS) as MemberSearchSortKey[];

const STATUS_LABELS: Record<Exclude<MemberSearchActiveStatus, ''>, string> = {
  active: 'Active',
  inactive: 'Inactive',
  terminated: 'Terminated',
  openEnrollment: 'Open Enrollment Members',
  gapCoverage: 'Gap Coverage',
};

const STATUS_KEYS = Object.keys(STATUS_LABELS) as Array<Exclude<MemberSearchActiveStatus, ''>>;

const DATE_SORT_KEYS: Set<MemberSearchSortKey> = new Set([
  'dateOfBirth',
  'activeDate',
  'deactivatedDate',
]);

function sortDirLabel(sortKey: MemberSearchSortKey, dir: 'asc' | 'desc'): string {
  if (DATE_SORT_KEYS.has(sortKey)) {
    return dir === 'asc' ? 'Oldest first' : 'Newest first';
  }
  return dir === 'asc' ? 'A–Z' : 'Z–A';
}

type MemberSearchResultRow = RouterOutput['vba']['members']['search']['members'][number];

type MemberSearchRowDisplay = MemberSearchResultRow & {id: string};

function formatDemographicsLine(row: MemberSearchRowDisplay): string {
  const city = row.city?.trim() ?? '';
  const stateVal = row.state?.trim() ?? '';
  const zip = row.zipCode?.trim() ?? '';
  if (!city && !stateVal && !zip) return '—';
  const cityState = [city, stateVal].filter(Boolean).join(', ');
  return [cityState, zip].filter(Boolean).join(' ');
}

type RowTag = {label: string; tooltip?: string; className: string};

/**
 * Row attributes surfaced as a Tags column of word pills (instead of the old
 * icon cluster in the Name column), mirroring the ops demo's TagPills pattern.
 */
function getRowTags(
  row: MemberSearchRowDisplay,
  tenantName?: string,
  showGapCoverage = true,
): RowTag[] {
  const tags: RowTag[] = [];
  if (row.isVip) {
    tags.push({label: 'VIP', className: 'bg-amber-50 text-amber-800'});
  }
  if (row.appUserId) {
    tags.push({
      label: 'App User',
      tooltip: tenantName ? `App user (${tenantName})` : undefined,
      className: 'bg-brand-blue/10 text-brand-blue',
    });
  }
  if (row.appUserEmailVerified) {
    tags.push({label: 'Email Verified', className: 'bg-sky-50 text-sky-700'});
  }
  if (row.isHsaProduct) {
    tags.push({
      label: 'HSA',
      tooltip: row.productCode ? `HSA (${row.productCode})` : undefined,
      className: 'bg-fuchsia-50 text-fuchsia-700',
    });
  }
  if (row.isMemberInOpenEnrollment) {
    tags.push({
      label: 'Open Enrollment',
      tooltip: 'User is currently in open enrollment',
      className: 'bg-indigo-50 text-indigo-700',
    });
  }
  if (showGapCoverage && row.isInGapCoverage) {
    tags.push({
      label: 'Gap Coverage',
      tooltip: row.gapCoverageEndDate
        ? `Gap coverage ends ${formatDate(row.gapCoverageEndDate, 'MM/DD/YYYY', '—')}`
        : 'In gap coverage',
      className: 'bg-sky-50 text-sky-700',
    });
  }
  return tags;
}

function MemberRowTenantBadge({name}: {name: string | null | undefined}) {
  if (!name) return null;
  return (
    <span className={cn(CBS_TENANT_BADGE_CLASS, 'shrink-0')} style={cbsTenantTint(name)}>
      {name}
    </span>
  );
}

function RowTagPills({tags}: {tags: RowTag[]}) {
  if (tags.length === 0) {
    return <span className='text-sm text-muted-foreground'>—</span>;
  }
  return (
    <div className='flex flex-wrap gap-1'>
      {tags.map((tag) => {
        const pill = (
          <span
            className={cn(
              'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
              tag.className,
            )}
          >
            {tag.label}
          </span>
        );
        if (!tag.tooltip) {
          return <span key={tag.label}>{pill}</span>;
        }
        return (
          <Tooltip key={tag.label}>
            <TooltipTrigger asChild>{pill}</TooltipTrigger>
            <TooltipContent>{tag.tooltip}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

function hasAdvancedFiltersState(s: MemberSearchUrlState): boolean {
  return Boolean(
    s.firstName ||
      s.lastName ||
      s.email ||
      s.zipCode ||
      s.phoneNumber ||
      // A pinned group surfaces its own banner (and hides the advanced group input),
      // so it shouldn't force the advanced panel open on its own.
      (!s.groupId && s.groupName) ||
      s.dateOfBirth ||
      s.effectiveStartFrom ||
      s.effectiveStartTo ||
      s.effectiveEndFrom ||
      s.effectiveEndTo ||
      s.excludeVip ||
      s.hasAppAccount,
  );
}

export function MemberSearch() {
  const {state, update} = useMemberSearchUrlState();
  const navigate = useNavigate();
  const memberDetailLinkState = useMemberDetailLinkState();
  const isGroupUser = useAuthStore((s) => s.isGroupUser());
  const isInternal = useIsInternal();
  const portal = usePortal();
  // Tenant name only exists on client portals; it enriches the "App User"
  // tag's tooltip. The internal portal has no tenant, so the tag stands alone.
  const tenantName = portal.portalType === 'client' ? portal.clientName : undefined;

  // Client (tenant) filter is internal-portal only — scoped users are already
  // constrained to their own tenant, so the dropdown would be a no-op for them.
  const clientsQuery = trpc.internal.invoice.getClients.useQuery(undefined, {
    enabled: isInternal,
    staleTime: STALE_TIME_5_MIN,
  });
  const clientOptions = clientsQuery.data?.clients ?? [];

  // Whether more than one administrator is in play for this viewer. The endpoint
  // returns nothing for anyone who can't see external-TPA data — the internal portal
  // included — so an empty list means every row is CBS and the "Administered by"
  // column would repeat the same value down the page.
  const externalTpasQuery = trpc.vba.members.listExternalTpas.useQuery(undefined, {
    staleTime: STALE_TIME_5_MIN,
  });
  const showAdministeredBy = (externalTpasQuery.data?.length ?? 0) > 0;

  const activeImportQuery = trpc.group.vbaMembers.activeEligibilityImport.useQuery(undefined, {
    refetchInterval: (query) => {
      if (!query.state.data) return false;
      return 5000;
    },
  });

  const [isExportOpen, setIsExportOpen] = useState(false);
  const activeExportQuery = useBackgroundMemberExport(isExportOpen);
  const debouncedQ = useDebounce(state.q, QUICK_SEARCH_DEBOUNCE_MS);
  // Bypass the debounce when the field is empty so the X button (and any other
  // explicit clear) reflects immediately instead of leaving stale results
  // visible for ~1s under `placeholderData: previous`.
  const effectiveQ = state.q === '' ? '' : debouncedQ;

  // SSN search is intentionally kept in local component state (not the URL) so the
  // value never lands in browser history, shareable links, or referrer headers.
  const [ssnQuery, setSsnQuery] = useState('');
  const debouncedSsn = useDebounce(ssnQuery, ADVANCED_FILTERS_DEBOUNCE_MS);
  const effectiveSsn = ssnQuery === '' ? '' : debouncedSsn;

  // Full-SSN reveal requires `phi.view_member_ssn_full` — the API pre-masks the
  // SSN for non-holders, so hiding the toggle just avoids a no-op reveal.
  const canRevealSsn = useHasPermission(CODE_PERMISSIONS.phi.viewMemberSsnFull.key);
  const canExportMembers = useClientActionAllowed(CODE_PERMISSIONS.members.exportCsv.key);
  const canAddMember = usePortalActionAllowed(CODE_PERMISSIONS.members.add.key);
  // Without `vip.manage` the API omits VIP rows from search results entirely,
  // so the "Hide VIP members" toggle would be a no-op.
  const canSeeVipRows = useClientActionAllowed(CODE_PERMISSIONS.vip.manage.key);
  const showVipToggle = !isGroupUser && canSeeVipRows;

  // Per-row reveal state for masked SSNs, keyed by `${subscriberId}-${memberSeq}`.
  const [revealedSsnRowIds, setRevealedSsnRowIds] = useState<Set<string>>(() => new Set());
  const toggleSsnReveal = useCallback((rowId: string) => {
    setRevealedSsnRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const advancedSlice: MemberSearchAdvancedSlice = useMemo(
    () => ({
      firstName: state.firstName,
      lastName: state.lastName,
      zipCode: state.zipCode,
      email: state.email,
      phoneNumber: state.phoneNumber,
      groupName: state.groupName,
      dateOfBirth: state.dateOfBirth,
      effectiveStartFrom: state.effectiveStartFrom,
      effectiveStartTo: state.effectiveStartTo,
      effectiveEndFrom: state.effectiveEndFrom,
      effectiveEndTo: state.effectiveEndTo,
    }),
    [
      state.firstName,
      state.lastName,
      state.zipCode,
      state.email,
      state.phoneNumber,
      state.groupName,
      state.dateOfBirth,
      state.effectiveStartFrom,
      state.effectiveStartTo,
      state.effectiveEndFrom,
      state.effectiveEndTo,
    ],
  );

  const debouncedAdvanced = useDebounce(advancedSlice, ADVANCED_FILTERS_DEBOUNCE_MS);

  const apiInputBase = useMemo(
    () =>
      memberSearchUrlStateToApiInput(state, {
        debouncedQ: effectiveQ,
        debouncedAdvanced,
        debouncedSsn: effectiveSsn,
      }),
    [
      state.activeStatus,
      state.relationship,
      state.clientId,
      state.excludeVip,
      state.groupId,
      state.externalTpa,
      state.sortKey,
      state.sortDir,
      state.hasAppAccount,
      state.notRegistered,
      state.hasVerifiedEmail,
      effectiveQ,
      debouncedAdvanced,
      effectiveSsn,
    ],
  );

  const apiInput = useMemo(
    () => ({
      ...apiInputBase,
      page: state.page,
    }),
    [apiInputBase, state.page],
  );

  const utils = trpc.useUtils();

  const {data, isLoading, isFetching, isPlaceholderData, error} = trpc.vba.members.search.useQuery(
    apiInput,
    {
      staleTime: STALE_TIME_30_SEC,
      placeholderData: (previous) => previous,
    },
  );

  const members = data?.members ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / MEMBER_SEARCH_PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (state.page - 1) * MEMBER_SEARCH_PAGE_SIZE + 1;
  const rangeEnd = Math.min(state.page * MEMBER_SEARCH_PAGE_SIZE, total);
  const hasNextPage = state.page < totalPages;
  const hasPrevPage = state.page > 1;

  useEffect(() => {
    if (total === 0) return;
    if (state.page > totalPages) {
      update({page: totalPages});
    }
  }, [total, totalPages, state.page, update]);

  // Warm neighbour pages once the current page is real (not placeholder) data.
  // react-query coalesces in-flight prefetches, so we don't need an `isFetching`
  // guard — gating on `!isPlaceholderData` just avoids prefetching from stale
  // filter/input combinations while a new search is resolving.
  useEffect(() => {
    if (isPlaceholderData) return;
    if (hasNextPage) {
      void utils.vba.members.search.prefetch({...apiInputBase, page: state.page + 1});
    }
    if (hasPrevPage) {
      void utils.vba.members.search.prefetch({...apiInputBase, page: state.page - 1});
    }
  }, [apiInputBase, hasNextPage, hasPrevPage, isPlaceholderData, state.page, utils]);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [quickViewVip, setQuickViewVip] = useState<QuickViewVip | null>(null);

  // VIP context resolves the manageable tenant for the current view (the user's
  // own client, or — for internal users — the selected client filter). When non-null,
  // the "Add VIP Member" button is shown and links to the form pre-scoped to that client.
  const vipContextQuery = trpc.vip.context.useQuery(
    {clientId: state.clientId || undefined},
    {enabled: !isGroupUser, staleTime: STALE_TIME_5_MIN},
  );
  const vipContext = vipContextQuery.data ?? null;

  const hasAdvanced = useMemo(
    () => hasAdvancedFiltersState(state),
    [
      state.firstName,
      state.lastName,
      state.email,
      state.zipCode,
      state.phoneNumber,
      state.groupName,
      state.groupId,
      state.dateOfBirth,
      state.effectiveStartFrom,
      state.effectiveStartTo,
      state.effectiveEndFrom,
      state.effectiveEndTo,
      state.excludeVip,
      state.hasAppAccount,
    ],
  );

  useEffect(() => {
    if (hasAdvanced) {
      setAdvancedOpen(true);
    }
  }, [hasAdvanced]);

  const clearAllFilters = useCallback(() => {
    // Filters reset; sort is part of the table's presentation and intentionally
    // preserved so users don't lose their chosen ordering when clearing filters.
    update({
      ...DEFAULT_MEMBER_SEARCH_STATE,
      sortKey: state.sortKey,
      sortDir: state.sortDir,
    });
    // SSN lives outside URL state, so reset it (and any revealed rows) separately.
    setSsnQuery('');
    setRevealedSsnRowIds(new Set());
  }, [update, state.sortKey, state.sortDir]);

  // cbs-portal active-filter chips (internal rendering only): each applied
  // filter surfaces as a soft-orange pill with an inline clear.
  const filterChips = useMemo(() => {
    if (!isInternal) return [];
    const chips: Array<{key: string; label: string; onClear: () => void}> = [];
    if (state.q)
      chips.push({key: 'q', label: `Search: ${state.q}`, onClear: () => update({q: '', page: 1})});
    if (state.clientId) {
      const clientName = clientOptions.find((c) => c.id === state.clientId)?.name ?? state.clientId;
      chips.push({
        key: 'client',
        label: `Client: ${clientName}`,
        onClear: () => update({clientId: '', page: 1}),
      });
    }
    if (state.activeStatus)
      chips.push({
        key: 'status',
        label: `Status: ${STATUS_LABELS[state.activeStatus]}`,
        onClear: () => update({activeStatus: '', page: 1}),
      });
    if (state.relationship) {
      const rel =
        RELATIONSHIP_OPTIONS.find((o) => o.value === state.relationship)?.label ??
        state.relationship;
      chips.push({
        key: 'relationship',
        label: `Relationship: ${rel}`,
        onClear: () => update({relationship: '', page: 1}),
      });
    }
    if (state.firstName)
      chips.push({
        key: 'firstName',
        label: `First name: ${state.firstName}`,
        onClear: () => update({firstName: '', page: 1}),
      });
    if (state.lastName)
      chips.push({
        key: 'lastName',
        label: `Last name: ${state.lastName}`,
        onClear: () => update({lastName: '', page: 1}),
      });
    if (state.email)
      chips.push({
        key: 'email',
        label: `Email: ${state.email}`,
        onClear: () => update({email: '', page: 1}),
      });
    if (state.zipCode)
      chips.push({
        key: 'zip',
        label: `ZIP: ${state.zipCode}`,
        onClear: () => update({zipCode: '', page: 1}),
      });
    if (!state.groupId && state.groupName)
      chips.push({
        key: 'groupName',
        label: `Group: ${state.groupName}`,
        onClear: () => update({groupName: '', page: 1}),
      });
    if (state.excludeVip)
      chips.push({
        key: 'excludeVip',
        label: 'Excluding VIPs',
        onClear: () => update({excludeVip: false, page: 1}),
      });
    if (state.hasAppAccount)
      chips.push({
        key: 'hasAppAccount',
        label: 'Has app account',
        onClear: () => update({hasAppAccount: false, page: 1}),
      });
    return chips;
  }, [isInternal, state, clientOptions, update]);

  return (
    /* Internal renders on the flat sand shell natively (no gradient), so it
       doesn't depend on the index.css repaint override. */
    <div
      className={
        isInternal
          ? 'min-h-screen'
          : 'min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'
      }
    >
      <PageHeader
        title='Member search'
        description='Filter and search for members across accessible groups.'
        icon={<Users className='h-6 w-6' />}
      >
        {canExportMembers && (
          <PageHeaderAction
            tone='secondary'
            onClick={() => setIsExportOpen(true)}
            disabled={total === 0 || (isFetching && isLoading)}
            title={total === 0 ? 'No matching members to export' : 'Export to CSV'}
          >
            <Download className='h-4 w-4 mr-2' />
            Export CSV
          </PageHeaderAction>
        )}
        {canAddMember && !isGroupUser && vipContext && (
          <PageHeaderAction
            tone='secondary'
            onClick={() =>
              navigate(`/members/add-vip?clientId=${encodeURIComponent(vipContext.clientId)}`)
            }
          >
            <Crown className='h-4 w-4 mr-2' />
            Add VIP Member
          </PageHeaderAction>
        )}
      </PageHeader>

      <div className='max-w-7xl mx-auto px-6 py-8 space-y-6'>
        {state.groupId && (
          <div className='flex flex-col gap-3 rounded-lg border border-brand-blue/20 bg-brand-blue/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
            <div className='flex min-w-0 items-center gap-2'>
              <Building2 className='h-5 w-5 shrink-0 text-brand-blue' aria-hidden />
              <p className='min-w-0 truncate text-sm text-brand-navy'>
                Showing members for{' '}
                <span className='font-semibold'>{state.groupName || `Group ${state.groupId}`}</span>
              </p>
            </div>
            <div className='flex shrink-0 items-center gap-2'>
              <Button type='button' variant='outline' size='sm' asChild>
                <Link
                  to={
                    state.externalTpa
                      ? ROUTES.externalTpaGroup.to(state.externalTpa, state.groupId)
                      : `/groups/${state.groupId}`
                  }
                >
                  <ArrowLeft className='h-4 w-4 mr-2' />
                  Back to group
                </Link>
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={() => update({groupId: '', groupName: '', externalTpa: '', page: 1})}
              >
                <X className='h-4 w-4 mr-2' />
                View all members
              </Button>
            </div>
          </div>
        )}

        {activeImportQuery.data && (
          <div className='flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3'>
            <FileSpreadsheet className='h-5 w-5 shrink-0 text-blue-600' />
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-medium text-blue-900'>
                Importing <span className='font-semibold'>{activeImportQuery.data.fileName}</span>
              </p>
              {activeImportQuery.data.progress ? (
                <div className='flex items-center gap-2 mt-1'>
                  <Progress
                    value={Math.round(
                      (activeImportQuery.data.progress.processed /
                        activeImportQuery.data.progress.total) *
                        100,
                    )}
                    className='h-1.5 flex-1'
                  />
                  <span className='text-xs text-blue-700 tabular-nums'>
                    {activeImportQuery.data.progress.processed}/
                    {activeImportQuery.data.progress.total}
                  </span>
                </div>
              ) : (
                <p className='text-xs text-blue-700 mt-0.5'>Queued — waiting to start…</p>
              )}
            </div>
            {activeImportQuery.data.status === 'RUNNING' && (
              <Loader2 className='h-4 w-4 shrink-0 animate-spin text-blue-500' />
            )}
          </div>
        )}

        {activeExportQuery.data && !isExportOpen && (
          <button
            type='button'
            onClick={() => setIsExportOpen(true)}
            className='w-full flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-left transition-colors hover:bg-emerald-100'
          >
            <Download className='h-5 w-5 shrink-0 text-emerald-600' />
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-medium text-emerald-900'>
                Exporting <span className='font-semibold'>{activeExportQuery.data.fileName}</span>
              </p>
              {activeExportQuery.data.progress ? (
                <div className='flex items-center gap-2 mt-1'>
                  <Progress
                    value={
                      activeExportQuery.data.progress.total > 0
                        ? Math.round(
                            (activeExportQuery.data.progress.processed /
                              activeExportQuery.data.progress.total) *
                              100,
                          )
                        : 0
                    }
                    className='h-1.5 flex-1'
                  />
                  <span className='text-xs text-emerald-700 tabular-nums'>
                    {activeExportQuery.data.progress.processed.toLocaleString()}/
                    {activeExportQuery.data.progress.total.toLocaleString()}
                  </span>
                </div>
              ) : (
                <p className='text-xs text-emerald-700 mt-0.5'>Queued — waiting to start…</p>
              )}
            </div>
            <Loader2 className='h-4 w-4 shrink-0 animate-spin text-emerald-500' />
          </button>
        )}

        <Card className='border-gray-200 shadow-sm'>
          <CardContent className='pt-6 space-y-4'>
            <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-6'>
              <div className='space-y-2 md:col-span-2'>
                <Label htmlFor='member-search-q'>Name or ID</Label>
                <div className='relative' aria-busy={isFetching}>
                  <Search
                    aria-hidden
                    className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray pointer-events-none'
                  />
                  <Input
                    id='member-search-q'
                    className='pl-9 pr-20'
                    placeholder='Search by name, or subscriber ID'
                    value={state.q}
                    onChange={(e) => update({q: e.target.value, page: 1})}
                    autoComplete='off'
                  />
                  <div className='absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1'>
                    {isFetching && isLoading && (
                      <Loader2
                        className='h-4 w-4 shrink-0 animate-spin text-brand-gray'
                        aria-label='Loading results'
                      />
                    )}
                    {state.q !== '' && (
                      <button
                        type='button'
                        className='rounded-sm p-1 text-brand-gray hover:bg-gray-100 hover:text-brand-navy focus-visible:outline focus-visible:ring-2 focus-visible:ring-brand-blue/30'
                        onClick={() => update({q: '', page: 1})}
                        aria-label='Clear search'
                      >
                        <X className='h-4 w-4' aria-hidden />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {isInternal && (
                <div className='space-y-2 md:col-span-2'>
                  <Label>Client</Label>
                  <Select
                    value={toSelectValue(state.clientId)}
                    onValueChange={(v) => update({clientId: fromSelectValue(v), page: 1})}
                    disabled={clientsQuery.isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='All clients' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_ALL}>All clients</SelectItem>
                      {clientOptions.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className='space-y-2'>
                <Label>Status</Label>
                <Select
                  value={toSelectValue(state.activeStatus)}
                  onValueChange={(v) =>
                    update({
                      activeStatus: fromSelectValue(v) as typeof state.activeStatus,
                      page: 1,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_ALL}>All</SelectItem>
                    {STATUS_KEYS.filter((status) => status !== 'gapCoverage' || !isInternal).map(
                      (status) => (
                        <SelectItem key={status} value={status}>
                          {STATUS_LABELS[status]}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2'>
                <Label>Relationship</Label>
                <Select
                  value={toSelectValue(state.relationship)}
                  onValueChange={(v) => update({relationship: fromSelectValue(v), page: 1})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_ALL}>All</SelectItem>
                    {RELATIONSHIP_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2 lg:col-span-2'>
                <Label>Sort</Label>
                <div className='flex gap-2'>
                  <Select
                    value={state.sortKey}
                    onValueChange={(v) => update({sortKey: v as MemberSearchSortKey, page: 1})}
                  >
                    <SelectTrigger className='flex-1 min-w-0'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SORTABLE_KEYS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {SORT_LABELS[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={state.sortDir}
                    onValueChange={(v) => update({sortDir: v as 'asc' | 'desc', page: 1})}
                  >
                    <SelectTrigger className='w-[140px] shrink-0'>
                      <SelectValue>{sortDirLabel(state.sortKey, state.sortDir)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='asc'>{sortDirLabel(state.sortKey, 'asc')}</SelectItem>
                      <SelectItem value='desc'>{sortDirLabel(state.sortKey, 'desc')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Collapsible
          open={advancedOpen}
          onOpenChange={setAdvancedOpen}
          className='rounded-xl border border-gray-200 bg-white shadow-sm'
        >
          <CollapsibleTrigger asChild>
            <button
              type='button'
              className='flex w-full items-center justify-between gap-3 px-6 py-4 text-left font-semibold text-brand-navy hover:bg-gray-50/80 rounded-t-xl transition-colors'
            >
              <span>Advanced search</span>
              <ChevronDown
                className={cn(
                  'h-5 w-5 shrink-0 text-brand-gray transition-transform duration-200',
                  advancedOpen && 'rotate-180',
                )}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className='border-t border-gray-100 px-6 pb-6 pt-2 space-y-6'>
              <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
                <div className='space-y-2'>
                  <Label htmlFor='member-search-fn'>First name</Label>
                  <Input
                    id='member-search-fn'
                    value={state.firstName}
                    onChange={(e) => update({firstName: e.target.value, page: 1})}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='member-search-ln'>Last name</Label>
                  <Input
                    id='member-search-ln'
                    value={state.lastName}
                    onChange={(e) => update({lastName: e.target.value, page: 1})}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='member-search-email'>Email</Label>
                  <Input
                    id='member-search-email'
                    type='email'
                    value={state.email}
                    onChange={(e) => update({email: e.target.value, page: 1})}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='member-search-zip'>ZIP</Label>
                  <Input
                    id='member-search-zip'
                    value={state.zipCode}
                    onChange={(e) => update({zipCode: e.target.value, page: 1})}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='member-search-phone'>Phone</Label>
                  <Input
                    id='member-search-phone'
                    inputMode='tel'
                    autoComplete='tel'
                    value={state.phoneNumber}
                    onChange={(e) => update({phoneNumber: e.target.value, page: 1})}
                  />
                </div>
                {!state.groupId && (
                  <div className='space-y-2'>
                    <Label htmlFor='member-search-group'>Group name or ID</Label>
                    <Input
                      id='member-search-group'
                      value={state.groupName}
                      onChange={(e) => update({groupName: e.target.value, page: 1})}
                    />
                  </div>
                )}
                <div className='space-y-2'>
                  <Label htmlFor='member-search-dob'>Date of birth</Label>
                  <DatePicker
                    id='member-search-dob'
                    value={state.dateOfBirth}
                    onChange={(iso) => update({dateOfBirth: iso, page: 1})}
                    calendarProps={{
                      captionLayout: 'dropdown',
                      startMonth: new Date(1920, 0),
                      endMonth: new Date(),
                    }}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='member-search-ssn'>SSN</Label>
                  <Input
                    id='member-search-ssn'
                    inputMode='numeric'
                    autoComplete='off'
                    placeholder='Full SSN or last 4'
                    value={ssnQuery}
                    onChange={(e) => {
                      setSsnQuery(e.target.value);
                      update({page: 1});
                    }}
                  />
                </div>
              </div>

              <div className='grid gap-4 md:grid-cols-2'>
                <fieldset className='space-y-2 rounded-lg border border-gray-200 p-4'>
                  <legend className='px-1 text-sm font-medium text-brand-navy'>
                    Effective start date
                  </legend>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-2'>
                      <Label htmlFor='member-search-eff-start-from'>From</Label>
                      <DatePicker
                        id='member-search-eff-start-from'
                        value={state.effectiveStartFrom}
                        onChange={(iso) => update({effectiveStartFrom: iso, page: 1})}
                        calendarProps={EFFECTIVE_DATE_CALENDAR_PROPS}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='member-search-eff-start-to'>To</Label>
                      <DatePicker
                        id='member-search-eff-start-to'
                        value={state.effectiveStartTo}
                        onChange={(iso) => update({effectiveStartTo: iso, page: 1})}
                        calendarProps={EFFECTIVE_DATE_CALENDAR_PROPS}
                      />
                    </div>
                  </div>
                </fieldset>
                <fieldset className='space-y-2 rounded-lg border border-gray-200 p-4'>
                  <legend className='px-1 text-sm font-medium text-brand-navy'>
                    Effective end date
                  </legend>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-2'>
                      <Label htmlFor='member-search-eff-end-from'>From</Label>
                      <DatePicker
                        id='member-search-eff-end-from'
                        value={state.effectiveEndFrom}
                        onChange={(iso) => update({effectiveEndFrom: iso, page: 1})}
                        calendarProps={EFFECTIVE_DATE_CALENDAR_PROPS}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='member-search-eff-end-to'>To</Label>
                      <DatePicker
                        id='member-search-eff-end-to'
                        value={state.effectiveEndTo}
                        onChange={(iso) => update({effectiveEndTo: iso, page: 1})}
                        calendarProps={EFFECTIVE_DATE_CALENDAR_PROPS}
                      />
                    </div>
                  </div>
                </fieldset>
              </div>

              {/* Paired on wider screens: neither needs a full row, and side by side
                  reads as the two result-scope switches they are. The VIP one is
                  permission-gated, so when it's absent the other spans the row rather
                  than sitting at half width beside empty space. */}
              <div className='grid gap-4 sm:grid-cols-2'>
                {showVipToggle && (
                  <div className='flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4'>
                    <div className='space-y-0.5'>
                      <Label htmlFor='member-search-exclude-vip' className='cursor-pointer'>
                        Hide VIP members
                      </Label>
                      <p className='text-sm text-brand-gray'>
                        Exclude CleverBenefits-only VIP members from the results.
                      </p>
                    </div>
                    <Switch
                      id='member-search-exclude-vip'
                      checked={state.excludeVip}
                      onCheckedChange={(checked) => update({excludeVip: checked, page: 1})}
                    />
                  </div>
                )}

                <div
                  className={cn(
                    'flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4',
                    !showVipToggle && 'sm:col-span-2',
                  )}
                >
                  <div className='space-y-0.5'>
                    <Label htmlFor='member-search-app-account' className='cursor-pointer'>
                      Has app account set up
                    </Label>
                    <p className='text-sm text-brand-gray'>
                      Only show members who have registered for the member app.
                    </p>
                  </div>
                  <Switch
                    id='member-search-app-account'
                    checked={state.hasAppAccount}
                    onCheckedChange={(checked) => update({hasAppAccount: checked, page: 1})}
                  />
                </div>
              </div>

              <div>
                <Button type='button' variant='outline' onClick={clearAllFilters}>
                  Clear filters
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {filterChips.length > 0 ? (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-[12px] text-muted-foreground'>Filtered by</span>
            {filterChips.map((chip) => (
              <span
                key={chip.key}
                className='inline-flex items-center gap-1.5 rounded-full bg-[#fdeedd] px-2.5 py-1 text-xs font-semibold text-[#d96f10]'
              >
                {chip.label}
                <button
                  type='button'
                  onClick={chip.onClear}
                  className='inline-flex items-center justify-center'
                  aria-label={`Clear ${chip.label} filter`}
                >
                  <X className='h-3 w-3' aria-hidden />
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <Card className='border-gray-200 shadow-sm overflow-hidden'>
          <div className='px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4'>
            <TableHeaderCount count={total} singular='member' plural='members' />
            {isFetching && !isLoading && (
              <div className='flex items-center gap-2 text-sm text-brand-gray'>
                <Loader2 className='h-4 w-4 animate-spin' aria-hidden />
                Loading…
              </div>
            )}
          </div>

          {error && (
            <div className='px-6 py-4 text-sm text-red-600'>
              {
                // Only surface the server-sent message when it's explicitly
                // user-facing validation; otherwise tRPC errors can leak
                // internals (stack fragments, ORM messages).
                error.data?.code === 'BAD_REQUEST' && error.message
                  ? error.message
                  : 'Failed to load members'
              }
            </div>
          )}

          <DataTable<MemberSearchRowDisplay>
            columns={[
              {
                id: 'name',
                header: 'Name',
                className: 'min-w-[200px]',
                render: (row: MemberSearchRowDisplay) => {
                  const displayName = `${row.lastName}, ${row.firstName}`;
                  // cbs-portal member cell: soft-orange initials avatar + tenant
                  // pill next to the name (internal rendering only).
                  const avatar = isInternal ? (
                    <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary'>
                      {initialsFromFullName(`${row.firstName} ${row.lastName}`)}
                    </span>
                  ) : null;
                  const tenantBadge = isInternal ? (
                    <MemberRowTenantBadge name={row.tenantName} />
                  ) : null;
                  if (row.isVip) {
                    return (
                      <div className='flex min-w-0 items-center gap-2.5'>
                        {avatar}
                        <div className='flex min-w-0 items-center gap-2'>
                          <span className='min-w-0 truncate font-medium text-brand-navy'>
                            {displayName}
                          </span>
                          {tenantBadge}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className='flex min-w-0 items-center gap-2.5'>
                      {avatar}
                      <div className='flex min-w-0 items-center gap-2'>
                        <Link
                          to={memberDetailPath(row)}
                          state={memberDetailLinkState}
                          className='min-w-0 truncate font-medium text-brand-navy hover:text-brand-blue hover:underline'
                          onClick={(e) => e.stopPropagation()}
                          onMouseEnter={() => {
                            // Warm the getDetails query the detail page reads.
                            // Not for TPA rows: they have no VBA subscriber, so the
                            // prefetch would be a guaranteed 404 on every hover.
                            if (row.externalTpa) return;
                            void utils.vba.members.getDetails.prefetch(
                              {subscriberId: row.subscriberId},
                              {staleTime: STALE_TIME_30_SEC},
                            );
                          }}
                        >
                          {displayName}
                        </Link>
                        {tenantBadge}
                      </div>
                    </div>
                  );
                },
              },
              {
                id: 'status',
                header: 'Status',
                render: (row: MemberSearchRowDisplay) => (
                  <span
                    className={cn(
                      'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold',
                      // Internal portal uses the demo's soft status tones.
                      isInternal
                        ? row.isActive
                          ? CBS_ACTIVE_PILL_CLASS
                          : CBS_INACTIVE_PILL_CLASS
                        : row.isActive
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-gray-100 text-gray-600',
                    )}
                  >
                    {row.isActive ? 'Active' : 'Inactive'}
                  </span>
                ),
              },
              {
                id: 'tags',
                header: 'Tags',
                className: 'min-w-[140px]',
                render: (row: MemberSearchRowDisplay) => (
                  <RowTagPills tags={getRowTags(row, row.tenantName ?? tenantName, !isInternal)} />
                ),
              },
              {
                id: 'relationship',
                header: 'Relationship',
                className: 'min-w-[100px]',
                headerClassName: 'min-w-[100px]',
                render: (row) => (
                  <span className='text-sm capitalize'>{row.relationshipLabel ?? '—'}</span>
                ),
              },
              {
                id: 'demographics',
                header: 'Demographics',
                className: 'min-w-[160px]',
                headerClassName: 'min-w-[160px]',
                render: (row) => (
                  <div className='space-y-0.5 text-sm'>
                    <div>{formatDate(row.birthDate, 'MM/DD/YYYY', '—')}</div>
                    <div className='text-muted-foreground'>{formatDemographicsLine(row)}</div>
                  </div>
                ),
              },
              // SSN stays searchable everywhere but is only displayed on the internal
              // portal; client and group users never see the column.
              ...(isInternal
                ? [
                    {
                      id: 'ssn',
                      header: 'SSN',
                      className: 'min-w-[140px]',
                      headerClassName: 'min-w-[140px]',
                      render: (row: MemberSearchRowDisplay) => {
                        if (!row.ssn) {
                          return <span className='text-sm text-muted-foreground'>—</span>;
                        }
                        const revealed = canRevealSsn && revealedSsnRowIds.has(row.id);
                        return (
                          <div className='flex items-center gap-1.5'>
                            <span className='text-sm tabular-nums'>
                              {revealed ? formatSsn(row.ssn) : maskSsn(row.ssn)}
                            </span>
                            {canRevealSsn && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type='button'
                                    className='rounded-sm p-1 text-brand-gray hover:bg-gray-100 hover:text-brand-navy focus-visible:outline focus-visible:ring-2 focus-visible:ring-brand-blue/30'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleSsnReveal(row.id);
                                    }}
                                    aria-label={revealed ? 'Hide SSN' : 'Show SSN'}
                                    aria-pressed={revealed}
                                  >
                                    {revealed ? (
                                      <EyeOff className='h-4 w-4' aria-hidden />
                                    ) : (
                                      <Eye className='h-4 w-4' aria-hidden />
                                    )}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {revealed ? 'Hide SSN' : 'Show SSN'}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        );
                      },
                    },
                  ]
                : []),
              {
                id: 'group',
                header: 'Group',
                className: 'min-w-[140px]',
                headerClassName: 'min-w-[140px]',
                render: (row) => (
                  <div className='space-y-0.5 text-sm'>
                    <div className='font-medium text-brand-navy'>{row.groupName ?? '—'}</div>
                    <div className='text-muted-foreground tabular-nums'>{row.groupId ?? '—'}</div>
                  </div>
                ),
              },
              ...(showAdministeredBy
                ? [
                    {
                      id: 'administeredBy',
                      header: 'Administered by',
                      render: (row: MemberSearchRowDisplay) =>
                        row.externalTpa ? (
                          <Badge className='bg-violet-50 text-violet-800 border-violet-200 font-medium'>
                            {row.externalTpaLabel}
                          </Badge>
                        ) : (
                          <Badge className='bg-brand-blue/10 text-brand-blue border-brand-blue/20 font-medium'>
                            {IN_HOUSE_ADMINISTRATOR.label}
                          </Badge>
                        ),
                    },
                  ]
                : []),
              {
                id: 'effectiveStart',
                header: 'Effective Start',
                render: (row) => formatDate(row.effectiveStart, 'MM/DD/YYYY', '—'),
              },
              {
                id: 'effectiveEnd',
                header: 'Effective End',
                render: (row) => (
                  <div>
                    <div>{formatDate(row.effectiveEnd, 'MM/DD/YYYY', '—')}</div>
                    {!isInternal && row.isInGapCoverage && row.gapCoverageEndDate && (
                      <div className='text-xs text-sky-700'>
                        Gap coverage ends {formatDate(row.gapCoverageEndDate, 'MM/DD/YYYY', '—')}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                id: 'actions',
                header: 'Actions',
                className: 'w-[100px]',
                headerClassName: 'w-[100px]',
                render: (row) => {
                  if (row.isVip) {
                    return (
                      <Button
                        variant='outline'
                        size='sm'
                        className='h-8'
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuickViewVip({
                            clientId: row.vipClientId,
                            uniqueId: row.vipUniqueId,
                            firstName: row.firstName,
                            lastName: row.lastName,
                          });
                        }}
                      >
                        View
                      </Button>
                    );
                  }
                  return (
                    <Button variant='outline' size='sm' className='h-8' asChild>
                      <Link
                        to={memberDetailPath(row)}
                        state={memberDetailLinkState}
                        onClick={(e) => e.stopPropagation()}
                      >
                        View
                      </Link>
                    </Button>
                  );
                },
              },
            ]}
            data={members.map(
              (m, index): MemberSearchRowDisplay => ({
                ...m,
                // VBA rows have a unique subscriberId+memberSeq. VIP rows are keyed
                // by their tenant-scoped vipUniqueId, but legacy/seed VIP documents
                // can share a placeholder id, so fold in the row index to keep the
                // React key unique across the rendered page.
                id: m.isVip
                  ? `vip-${m.vipClientId}-${m.vipUniqueId}-${index}`
                  : `${m.subscriberId}-${m.memberSeq}`,
              }),
            )}
            loading={isLoading}
            emptyMessage='No members match your search.'
          />

          {total > 0 && (
            <div className='flex flex-col gap-3 border-t border-gray-100 bg-gray-50/50 sm:flex-row sm:items-center sm:justify-between px-6 py-4'>
              <p className='text-sm text-brand-gray tabular-nums'>
                Showing <span className='font-medium text-brand-navy'>{rangeStart}</span>–
                <span className='font-medium text-brand-navy'>{rangeEnd}</span> of{' '}
                <span className='font-medium text-brand-navy'>{total}</span>
              </p>
              {/*
                Pagination is gated by `isLoading` (no data yet) rather than
                `isFetching`, so users can keep clicking Prev/Next during
                placeholder-data refetches instead of waiting for each page
                to resolve before queueing the next one.
              */}
              <div className='flex items-center gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={state.page <= 1 || isLoading}
                  onClick={() => update({page: state.page - 1})}
                >
                  Previous
                </Button>
                <span className='text-sm text-brand-gray tabular-nums px-1'>
                  Page {state.page} of {totalPages}
                </span>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={!hasNextPage || isLoading}
                  onClick={() => update({page: state.page + 1})}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>

        <VipQuickViewSheet
          vip={quickViewVip}
          onOpenChange={(open) => {
            if (!open) setQuickViewVip(null);
          }}
        />
      </div>

      <MemberExportDialog
        open={isExportOpen}
        onOpenChange={setIsExportOpen}
        filters={isExportOpen ? apiInputBase : null}
      />
    </div>
  );
}
